import * as React from 'react';
import InputHandler from 'Components/InputHandler';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import ActionButton from 'Components/ActionButton';
import {
  dialogContent,
  dialogActions,
  dialogCancel,
  dialogTitle,
} from '../../../../../../../config/theme/componentTheme';
import FormRow from 'Components/FormRow';
import { nowPartialDate } from 'neuro-utils';
import { actions } from 'Store/myapp/actions';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'Store/index';
import browserLanguage from 'Utility/browserLanguage';
import { partialDateToEndExclusive, partialDateToStartInclusive } from 'Utility/tasklist';

const TaskListDialog = ({
  taskActionType,
  sessionDate,
  dialogOpen,
  setDialogOpen,
  activeTaskLists,
  fm,
}: IOwnProps): React.JSX.Element => {
  const dispatch = useDispatch();

  const platform = useAppSelector(s => s.session.platforms?.selected);
  const locale = useAppSelector(s => s.settings.userSettings.uiLanguage) ?? browserLanguage;
  const taskUiSettings = useAppSelector(s => s.settings.orgSettings.taskUiSettings);
  const platformSettings = platform ? taskUiSettings?.[platform] : undefined;

  const initData: ITaskListData = {
    template_id: 'treatmentEfficiency',
    start_inclusive: partialDateToStartInclusive(nowPartialDate()) as string,
    end_exclusive: partialDateToEndExclusive(sessionDate ?? nowPartialDate()) as string,
    tasks: [
      taskActionType === 'tdcsTreatmentEfficiencyBefore' ? 'treatmentEfficiencyBefore' : 'treatmentEfficiencyAfter',
    ],
    description: platformSettings?.treatmentEfficiency?.descriptionLocalized[locale] ?? '',
    reminders_before_end: [],
    alerts_after_start: [],
  };

  // Temporary data when dialog is open
  const [data, setData] = React.useState<ITaskListData>(initData);

  const handleChange = (e: TOnChangeValues) => {
    const name = Object.keys(e)[0];
    if (!name) return;
    const value = e[name];
    setData((prev: typeof data) => ({ ...prev, [name]: value }));
  };

  const handleSend = () => {
    const taskListData = data as ITaskList;

    // If creating tasklist for tretamentEfficiencyAfter, update existing tasklist instead of creating new one
    if (taskListData.tasks.some(task => task.includes('After'))) {
      taskListData.tasks = ['treatmentEfficiencyBefore', 'treatmentEfficiencyAfter'];

      const beforeTaskList = activeTaskLists.find(tl => tl.tasks.includes('treatmentEfficiencyBefore'));

      actions
        .updateTasklist({ ...beforeTaskList, ...taskListData }, dispatch)
        .then(() => setDialogOpen(false))
        .catch(() => null); // log?
    } else {
      actions
        .newTasklist(taskListData, dispatch)
        .then(() => setDialogOpen(false))
        .catch(() => null); // log?
    }
  };

  // Reset data when dialog is opened (again)
  React.useEffect(() => {
    setData(initData);
  }, [dialogOpen]);

  return (
    <Dialog
      open={dialogOpen}
      maxWidth="sm"
      fullWidth={true}
      PaperProps={{ square: true }}
      onClose={() => setDialogOpen(false)}
    >
      <DialogTitle style={dialogTitle}>{fm('myService.taskList')}</DialogTitle>
      <DialogContent style={dialogContent}>
        <FormRow title="myService.tasksForTaskList">
          <InputHandler
            name="tasks"
            type="Checkbox"
            editing={false}
            options={['treatmentEfficiencyBefore', 'treatmentEfficiencyAfter']}
            optionFormatter={(name: string | number) =>
              name
                ? `${fm('myService.ninmt.treatmentEfficiency')} ${fm(
                    `tdcs.patientsRating.${(name as string).indexOf('Before') > -1 ? 'before' : 'after'}Treatment`,
                  ).toLocaleLowerCase()}`
                : '-'
            }
            formData={{ document: { tasks: data.tasks }, onChange: handleChange }}
          />
        </FormRow>
        <FormRow title="myService.taskListInfo">
          <InputHandler
            name="description"
            type="TextArea"
            editing={true}
            formData={{ document: { description: data.description }, onChange: handleChange }}
            placeholder={fm('myService.taskListInfoPlaceholder')}
            disablePlaceholderFormatting
            rows={14}
            maxHeight={25}
          />
        </FormRow>
      </DialogContent>
      <DialogActions style={{ ...dialogActions, justifyContent: 'flex-end' }}>
        <div>
          <div style={{ ...dialogCancel, display: 'inline-block' }} onClick={() => setDialogOpen(false)}>
            {fm('general.cancel')}
          </div>
          <div style={{ display: 'inline-block' }}>
            <ActionButton text="myService.send" onClick={handleSend} width={12} height={3} fontSize={16} />
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
};

/** The data used to compile a tasklist */
// oxlint-disable-next-line typescript/no-empty-interface
interface ITaskListData extends Pick<
  ITaskList,
  | 'template_id'
  | 'start_inclusive'
  | 'end_exclusive'
  | 'tasks'
  | 'description'
  | 'reminders_before_end'
  | 'alerts_after_start'
  // oxlint-disable-next-line typescript/no-empty-object-type
> {}

interface IOwnProps {
  taskActionType: 'tdcsTreatmentEfficiencyBefore' | 'tdcsTreatmentEfficiencyAfter';
  sessionDate?: PartialDate;
  dialogOpen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  activeTaskLists: Array<ITaskList>;
  fm: (m: string) => string;
}

export default TaskListDialog;
